import React, { useEffect, useRef, useState } from 'react';
import ScreenWrapper from '@hoc/ScreenWrapper';
import { Platform, ScrollView, TouchableOpacity } from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { PrivateStackParamList } from '@routes/types';
import CommonHeader from '@sharedComponents/Header/Header';
import { ThemedView, ThemedText, Theme } from '@app/theme';
import GreenInfo from '../../../assets/Icons/GreenInfo';
import Button from '@sharedComponents/Button/Button';
import { useTheme } from '@shopify/restyle';
import DSPIcon from '../../../assets/Icons/DSPIcon';
import {
  activateAssetAPI,
  lenderInfoAPI, loanOfferDetailsAPICall,
} from '@screens/LoanOfferEnhanceLimit/services/loanOfferEnhanceLimitService';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import PageLoader from '@sharedComponents/PageLoader/PageLoader';
import { addCommasToNumber, addPrefixToMonth } from '@app/utils';
import { getDateMonthYearFromEpoch } from '@app/sharedComponents/stack/utils';
import ErrorCard from '@screens/LoanOfferEnhanceLimit/components/ErrorCard';
import InfoIcon from '../../../assets/Icons/InfoIcon';
import BottomSheetContainer from '@sharedComponents/Bottomsheet/Bottomsheet';
import SystemErrorIcon from '../../../assets/Icons/SystemErrorIcon';
import { LENDER_ID } from '@app/types/regix';
import TataLogo from '@app/assets/svg/TataLogo';
import ExistingCreditLimitTooltip from '@screens/LoanOfferEnhanceLimit/components/ExistingCreditLimitTooltip';
import handleApiErr from '@app/helper/handleApiErr';
import { showErrorSheet } from '@app/store/slices/errorSlice';
import BajajLogo from "../../../assets/Icons/BajajLogo";


export type ScreenProps = NativeStackScreenProps<PrivateStackParamList, 'LOAN_OFFER_ENHANCE_LIMIT'>;

const LoanOfferEnhanceLimit = ({ navigation, route }: ScreenProps) => {
  const dispatch = useAppDispatch();
  const { colors } = useTheme<Theme>();
  const { userDetails } = useAppSelector(state => state.auth);
  const [pageLoader, setPageLoader] = useState(false);
  const [pledgeData, setPledgeData] = useState<any>();
  const [lenderInfo, setLenderInfo] = useState<any>();
  const [showErrorCard, setShowErrorCard] = useState(false);
  const [errorBody, setErrorBody] = useState({
    title: '',
    message: '',
  });
  const [ctaLoader, setCTALoader] = useState(false);
  const [showInterestAndChargesCard, setShowInterestAndChargesCard] = useState(false);
  const [showExistingCreditLimitTooltip, setShowExistingCreditLimitTooltip] = useState(false);

  const fromSetCreditLimit = route?.params?.source === 'SET_CREDIT_LIMIT';

  const isLenderDSP = lenderInfo?.lenderAccountId.toUpperCase() === LENDER_ID.DSP.toUpperCase();
  const isLenderTATA = lenderInfo?.lenderAccountId.toUpperCase() === LENDER_ID.TATA.toUpperCase();
  const isLenderBajaj = lenderInfo?.lenderAccountId.toUpperCase() === LENDER_ID.BAJAJ.toUpperCase();

  

  const getPledgeAssetDataAPICall = async () => {
    try {
      const response = await loanOfferDetailsAPICall({
        applicationId: userDetails?.linkedApplications[0]?.applicationId,
      });
      setPledgeData(response?.data);
    } catch (error) {

    }
  };

  const handleConfirmCTA = async () => {
    setCTALoader(true);
    try {
      const activateAssetAPIResponse = await activateAssetAPI({
        applicationId: userDetails?.linkedApplications[0]?.applicationId,
      });
      const currentStepId = activateAssetAPIResponse?.data?.updatedApplicationObj?.currentStepId;
      setCTALoader(false);
      if (Platform.OS === 'web' || !fromSetCreditLimit) {
        navigation.navigate(currentStepId);
      } else {
        navigation.reset({
          index: 0,
          routes: [{ name: "HOME" }, { name: 'KYC_STEPPER' }, { name: currentStepId }]
        })
      }
    } catch (error: any) {
      setCTALoader(false);
      //@ts-ignore
      // if (error?.data?.voltErrorCode === 'CREDIT_LIMIT_EXCEEDED') {
      //   setShowErrorCard(true);
      //   setErrorBody({
      //     title: 'Total credit limit cannot exceed ₹2Cr',
      //     message: 'We\'ll notify you once higher credit limits can be provided',
      //   });
      // } else {
        if(error?.data?.statusCode && handleApiErr(error)) {
            dispatch(showErrorSheet({
                isErrorSheetVisible: true,
                errorMessage: error?.data?.message,
                closeOnBackdropPress: true,
                errorType: error?.data?.voltErrorCode || error?.data?.statusCode,
                errorHeading: error?.data?.header,
                showErrorHeading: true,
                btnName: 'Ok, got it!',
            }))
          // }
        }
      }
  };

  const renderInterestCharges = () => {
    return (
      <BottomSheetContainer
        isVisible={showInterestAndChargesCard}
        close={() => {
          setShowInterestAndChargesCard(false);
        }}
        onClose={() => {
          setShowInterestAndChargesCard(false);
        }}
        dragComponent={undefined}
        closeOnBackdropPress={true}
        borderTopRightRadius={10}
        borderTopLeftRadius={10}
        containerStyle={{ backgroundColor: '#fefefe' }}
        showCloseIcon={true}
      >
        <ThemedView padding={'lg'}>
          <ThemedText variant={'h4s'} color={'text-primary'}>
            Interest and charges
          </ThemedText>
          <ThemedView mt={'lg'} padding={'md'} borderRadius={'md'} borderWidth={1} borderColor={'border-primary'}
                      mb={'xl'}>
            {pledgeData?.stepResponseObject?.processingFeesBreakUp?.map((item: any) => (
              <ThemedView key={item.index}>
                <ThemedView flexDirection={'row'} justifyContent={'space-between'}>
                  <ThemedText variant={'b3r'} color={'text-secondary'}>
                    {item.chargeType}
                  </ThemedText>
                  <ThemedText variant={'b3s'} color={'text-primary'}>
                    ₹{addCommasToNumber(item.amount)}
                  </ThemedText>
                </ThemedView>
                {item.index < pledgeData?.stepResponseObject?.processingFeesBreakUp.length - 1 && (
                  <ThemedView height={1} backgroundColor={'border-primary'} marginVertical={'md'} />
                )}
              </ThemedView>
            ))}
            <ThemedView mt='md' borderTopWidth={1} borderColor={'border-primary'} paddingTop={'md'}>
                <ThemedView flexDirection={'row'} justifyContent={'space-between'}>
                  <ThemedText variant={'b3s'} color={'text-primary'}>
                    Total
                  </ThemedText>
                  <ThemedText variant={'b3s'} color={'text-primary'}>
                    {`₹${addCommasToNumber(pledgeData?.stepResponseObject?.totalProcessingFees)}`}
                  </ThemedText>
                </ThemedView>
              </ThemedView>
          </ThemedView>
          <Button
            label="Ok, got it!"
            type="SOLID"
            size="MD"
            disabled={false}
            handlePress={() => {
              setShowInterestAndChargesCard(false);
            }}
            bg={colors['bg-action-primary']}
            textColor={colors['text-primary']}
          />
        </ThemedView>
      </BottomSheetContainer>
    );
  };

  const getLenderInfoAPICall = async () => {
    const response = await lenderInfoAPI({
      applicationId: userDetails?.linkedApplications[0]?.applicationId,
    });
    setLenderInfo(response?.data);
  };

  useEffect(() => {
    setPageLoader(true);
    Promise.all([
      getPledgeAssetDataAPICall(),
      getLenderInfoAPICall(),
    ]).finally(() => {
      setPageLoader(false);
    });
  }, []);

  const viewRef = useRef(null);



  return (
    <ScreenWrapper screenName={route?.name} additionalProperties={{ newArchRoute: true }}>
      {pageLoader ? <PageLoader /> :
        <>
          <CommonHeader label="Verify interest and charges" navigation={navigation} pageName={route.name} />
          <ThemedView backgroundColor={"bg-surface-page"} flex={1}>
            <ScrollView>
              <ThemedView mt={'xl'} padding={'md'} backgroundColor={'bg-surface-primary'} mb={'lg'}>
                <ThemedText variant={'b4r'} color={'text-secondary'}>
                  Selected credit limit
                </ThemedText>
                <ThemedText variant={'h2b'} color={'text-emphasis-secondary'} mt={'xs'}>
                  <ThemedText fontFamily='Inter' variant={'h2b'} color={'text-emphasis-secondary'}>₹</ThemedText>
                  {`${addCommasToNumber(Number(pledgeData?.stepResponseObject?.requestCreditLimit))}`}
                </ThemedText>
                <ThemedView height={1} backgroundColor={'border-secondary'} marginVertical={'md'} />
                <ThemedText variant={'b4r'} color={'text-secondary'}>
                  Selected mutual funds
                </ThemedText>
                <ThemedText variant={'b2b'} color={'text-primary'} mt={'xs'}>
                  {`₹${addCommasToNumber(pledgeData?.stepResponseObject?.requestedPortfolioValue)}`}
                </ThemedText>
              </ThemedView>
              <ThemedText variant={'h5s'} color={'text-primary'} paddingHorizontal={'lg'}
                          paddingVertical={'md'}>
                Updated credit limit
              </ThemedText>
              <ThemedView paddingVertical={'lg'} paddingHorizontal={'md'}
                          backgroundColor={'bg-surface-primary'}
                          mb={'lg'}>
                <ThemedView flexDirection={'row'} justifyContent={'space-between'}>
                  <ThemedView flexDirection={'row'} alignItems={'center'}>
                    <ThemedText variant={'b3r'} color={'text-primary'} mr={'sm'}>
                      Existing credit limit
                    </ThemedText>
                    <TouchableOpacity style={{ position: 'relative' }} ref={viewRef} activeOpacity={1} onPress={() => {
                      setShowExistingCreditLimitTooltip(!showExistingCreditLimitTooltip);
                    }}>
                      <InfoIcon size={18} fill={'#98A2B3'} bgColor={'#FFFFFF'}/>
                      {showExistingCreditLimitTooltip && <ExistingCreditLimitTooltip onClose={() => {
                        setShowExistingCreditLimitTooltip(false);
                      }} />}
                    </TouchableOpacity>
                  </ThemedView>

                  <ThemedText variant={'b3s'} color={'text-primary'}>
                    {`₹${addCommasToNumber(pledgeData?.stepResponseObject?.existingDrawingPower)}`}
                  </ThemedText>
                </ThemedView>
                <ThemedView height={1} backgroundColor={'border-secondary'} marginVertical={'md'} zIndex={-10} />

                <ThemedView flexDirection={'row'} justifyContent={'space-between'} zIndex={-10}>
                  <ThemedText variant={'b3r'} color={'text-primary'}>
                    Additional credit limit
                  </ThemedText>
                  <ThemedText variant={'b3s'} color={'text-primary'}>
                    {`₹${addCommasToNumber(pledgeData?.stepResponseObject?.requestCreditLimit)}`}
                  </ThemedText>
                </ThemedView>
                <ThemedView height={1} backgroundColor={'border-secondary'} marginVertical={'md'} zIndex={-10}/>

                <ThemedView flexDirection={'row'} justifyContent={'space-between'} zIndex={-10}>
                  <ThemedText variant={'b3r'} color={'text-primary'}>
                    Total credit limit
                  </ThemedText>
                  <ThemedText variant={'b3s'} color={'text-primary'}>
                    {`₹${addCommasToNumber(pledgeData?.stepResponseObject?.drawingPower)}`}
                  </ThemedText>
                </ThemedView>


              </ThemedView>
              <ThemedText variant={'h5s'} color={'text-primary'} paddingHorizontal={'lg'}
                          paddingVertical={'md'}>
                Interest and charges
              </ThemedText>
              <ThemedView paddingHorizontal={'md'} paddingVertical={'lg'}
                          backgroundColor={'bg-surface-primary'}
                          mb={'lg'}>
                <ThemedView flexDirection={'row'} justifyContent={'space-between'}>
                  <ThemedText variant={'b3r'} color={'text-primary'}>
                    Interest rate
                  </ThemedText>
                  <ThemedText variant={'b3s'} color={'text-primary'}>
                    {`${pledgeData?.stepResponseObject?.interestRate}%`}

                  </ThemedText>
                </ThemedView>
                <ThemedView height={1} backgroundColor={'border-secondary'} marginVertical={'md'} />

                <ThemedView flexDirection={'row'} justifyContent={'space-between'}>
                  <ThemedView flexDirection={'row'} alignItems={'center'}>
                    <ThemedText variant={'b3r'} color={'text-primary'} mr={'sm'}>
                      Total charges
                    </ThemedText>
                    <TouchableOpacity activeOpacity={1} onPress={() => {
                      setShowInterestAndChargesCard(!showInterestAndChargesCard);
                    }}>
                      <InfoIcon size={18} fill={'#98A2B3'} bgColor={'#FFFFFF'}/>
                    </TouchableOpacity>
                  </ThemedView>

                  <ThemedText variant={'b3s'} color={'text-primary'}>
                    {`₹${addCommasToNumber(pledgeData?.stepResponseObject?.totalProcessingFees)}`}
                  </ThemedText>
                </ThemedView>
              </ThemedView>

              <ThemedText variant={'h5s'} color={'text-primary'} paddingHorizontal={'lg'}
                          paddingVertical={'md'}>
                Repayment schedule
              </ThemedText>

              <ThemedView paddingHorizontal={'md'} paddingVertical={'lg'}
                          backgroundColor={'bg-surface-primary'}
                          mb={'lg'}>
                <ThemedView flexDirection={'row'} justifyContent={'space-between'}>
                  <ThemedText variant={'b3r'} color={'text-primary'}>
                    Loan expires on
                  </ThemedText>
                  <ThemedText variant={'b3s'} color={'text-primary'}>
                    {getDateMonthYearFromEpoch(pledgeData?.stepResponseObject?.loanEndDate)}
                  </ThemedText>
                </ThemedView>
                <ThemedView height={1} backgroundColor={'border-secondary'} marginVertical={'md'} />

                <ThemedView flexDirection={'row'} justifyContent={'space-between'}>
                  <ThemedText variant={'b3r'} color={'text-primary'}>
                    Interest autopay
                  </ThemedText>
                  <ThemedText variant={'b3s'} color={'text-primary'}>
                    {addPrefixToMonth(lenderInfo?.lenderBusinessDetails?.autopayDate)}
                  </ThemedText>
                </ThemedView>

                <ThemedView height={1} backgroundColor={'border-secondary'} marginVertical={'md'} />

                <ThemedView flexDirection={'row'} justifyContent={'space-between'}>
                  <ThemedText variant={'b3r'} color={'text-primary'}>
                    Principal repayment
                  </ThemedText>
                  <ThemedText variant={'b3s'} color={'text-primary'}>
                    Flexible
                  </ThemedText>
                </ThemedView>

              </ThemedView>

            </ScrollView>
            <ThemedView justifyContent={'flex-end'} backgroundColor={'bg-surface-primary'}>
              <ThemedView height={1} backgroundColor={'border-secondary'} />

              <ThemedView padding={'lg'}>
                <Button
                  label="Continue"
                  type="SOLID"
                  size="MD"
                  disabled={false}
                  handlePress={handleConfirmCTA}
                  bg={colors['bg-action-primary']}
                  buttonLoader={ctaLoader}
                  textColor={colors['text-primary']}
                />
                <ThemedView justifyContent='center' mt={'md'} alignSelf={'center'} alignItems={'center'} flexDirection={'row'}>
                  <ThemedText variant={'b4r'} color={'text-primary'} mr={'sm'}>
                    LENDING PARTNER
                  </ThemedText>
                  <ThemedView>
                    {isLenderDSP &&
                      <DSPIcon color1={colors['icon-primary']}/>
                    }
                    {isLenderTATA &&
                      <TataLogo />
                    }
                    {isLenderBajaj &&
                      <BajajLogo />
                    }
                  </ThemedView>
                </ThemedView>

              </ThemedView>

            </ThemedView>
          </ThemedView>
        </>}
      {showErrorCard &&
        <ErrorCard failedTitle={errorBody.title} failedSubTitle={errorBody.message} colors={colors} onClose={() => {
          setShowErrorCard(false);
        }} visible={showErrorCard} />}
      {/* {showInterestAndChargesCard && renderInterestCharges()} */}
      {renderInterestCharges()}
    </ScreenWrapper>
  );
};

export default LoanOfferEnhanceLimit;